import {
  Alert,
  Card,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import CsvIcon from "../../../../../../src/assets/csvicon.svg";
import readXlsxFile from "read-excel-file";
import {
  formatDate,
  formatDateToYYYYMMDD,
} from "../helpers/funcs/formatDateForCSVandXLSX";
import { useDispatch } from "react-redux";
import { setUploadReceiptsPayload } from "../../../../../features/financeReceiptSlice";
import { useSelector } from "react-redux";
interface AddReceiptsUploadFileProps {
  uploadedFiles: any[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<any[]>>;
  parsedRows: any[];
  setParsedRows: React.Dispatch<React.SetStateAction<any[]>>;
}

const AddReceiptsUploadFile = ({
  uploadedFiles,
  setUploadedFiles,
  parsedRows,
  setParsedRows,
}: AddReceiptsUploadFileProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const dispatch = useDispatch();
  const allCompanies = useSelector((state: any) => state.receipts.allCompanies);
  const allCurrencies = useSelector(
    (state: any) => state.receipts.allCurrencies,
  );

  useEffect(() => {
    const cleanPayload = parsedRows.map(
      ({ _fileId, receipt_amount, ...rest }) => ({
        ...rest,
        receipt_amount:
          receipt_amount !== undefined &&
          receipt_amount !== null &&
          receipt_amount !== ""
            ? Number(receipt_amount)
            : null,
      }),
    );
    dispatch(setUploadReceiptsPayload(cleanPayload));
  }, [parsedRows, dispatch]);

  const normalize = (s: any) =>
    (typeof s === "string" ? s.trim() : (s ?? "")).toString();
  const getField = (row: any, variants: string[]) => {
    for (const v of variants) {
      if (row[v] !== undefined) return row[v];
      // try lower / upper keys
      const lower = v.toLowerCase();
      const found = Object.keys(row).find((k) => k.toLowerCase() === lower);
      if (found) return row[found];
    }
    return undefined;
  };

  const findCurrencyByCode = (code: string) => {
    if (!code) return null;
    const upper = code.trim().toUpperCase();
    return allCurrencies.find(
      (c: any) => normalize(c.currency_code).toUpperCase() === upper,
    );
  };

  const findCompanyByFinanceId = (id: any) => {
    if (!id) return null;
    const numId = Number(id);
    if (Number.isNaN(numId)) return null;

    return (
      allCompanies.find((c: any) => Number(c.finance_company_id) === numId) ||
      null
    );
  };

  const transformRow = (rawRow: any, fileId: string) => {
    // possible header variants may be delete later
    const receiptId =
      getField(rawRow, ["receipt_id", "receipt id", "receipt"]) ?? "";
    const receiptDate =
      getField(rawRow, ["receipt_date", "receipt date", "date"]) ?? "";
    const receiptAmount =
      getField(rawRow, ["receipt_amount", "amount", "receipt amount"]) ?? "";
    const currencyCode =
      getField(rawRow, ["currency_code", "currency", "currency code"]) ?? "";
    const financeCompanyId = getField(rawRow, ["finance_company_id"]) ?? "";
    const receiptStatus =
      getField(rawRow, ["receipt_status", "status", "receipt status"]) ?? "";

    // Validate/match currency
    const currency = findCurrencyByCode(normalize(currencyCode));
    if (!currency) {
      return {
        success: false,
        error: `Currency code not found.`,
      };
    }
    if (!receiptId || String(receiptId).trim() === "") {
      return {
        success: false,
        error: `receipt_id is required.`,
      };
    }

    const formattedDate = formatDateToYYYYMMDD(receiptDate);
    if (!formattedDate) {
      return {
        success: false,
        error: `Invalid receipt_date "${normalize(
          receiptDate,
        )}". Expected format YYYY-MM-DD.`,
      };
    }

    const numAmount = Number(String(receiptAmount).replace(/[, ]+/g, ""));
    if (!receiptAmount || isNaN(numAmount)) {
      return {
        success: false,
        error: `Invalid receipt_amount "${normalize(
          receiptAmount,
        )}". Must be a number.`,
      };
    }

    if (!receiptStatus || String(receiptStatus).trim() === "") {
      return {
        success: false,
        error: `receipt_status is required.`,
      };
    }

    // Validate/match company
    if (!financeCompanyId) {
      return {
        success: false,
        error: `finance_company_id is required.`,
      };
    }

    const company = findCompanyByFinanceId(financeCompanyId);

    if (!company) {
      return {
        success: false,
        error: `Company with finance_company_id "${financeCompanyId}" not found.`,
      };
    }
    // Compose transformed row
    const transformed: any = {
      _fileId: fileId,
      receipt_id: normalize(receiptId),
      receipt_date: formattedDate,
      receipt_amount: receiptAmount,
      receipt_currency_shared_uid: currency.shared_uid,
      company_shared_uid: company.shared_uid,
      receipt_status: normalize(receiptStatus),
    };

    return { success: true, row: transformed };
  };

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (!lines.length) return [];
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cols = line.split(",");
      const obj: any = {};
      headers.forEach((h, i) => (obj[h] = cols[i]));
      return obj;
    });
  };

  const parseXLSX = async (file: File): Promise<any[]> => {
    const rows = await readXlsxFile(file); // rows: any[][]
    if (!rows.length) return [];
    const [headerRow, ...dataRows] = rows;
    const headers = headerRow.map((h) => String(h).trim());
    const result = dataRows.map((row) => {
      const obj: any = {};
      headers.forEach((h, i) => {
        const value = row[i];
        if (value instanceof Date) {
          // Excel date cell
          obj[h] = formatDate(value);
        } else {
          obj[h] = value ?? "";
        }
      });
      return obj;
    });
    return result;
  };

  //Functions for checking file type that the file is .csv or .xlsx
  const isCsvFile = (file: File) => {
    const name = file.name.toLowerCase();
    const type = file.type;
    return (
      name.endsWith(".csv") ||
      name.endsWith(".xlsx") ||
      type === "text/csv" ||
      type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || // xlsx MIME
      type === "application/vnd.ms-excel" // sometimes used for csv/xlsx
    );
  };

  // Handle file upload via input
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    files.forEach((file) => {
      if (!isCsvFile(file)) {
        setSnackbarMsg(
          "Invalid file type — only .csv and .xlsx files are allowed.",
        );
        setSnackbarOpen(true);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSnackbarMsg("File size exceeds 5MB limit.");
        setSnackbarOpen(true);
        return;
      }
      processFile(file);
    });
    e.target.value = "";
  };

  // Handle file upload via drag and drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    files.forEach((file) => {
      if (!isCsvFile(file)) {
        setSnackbarMsg(
          "Invalid file type — only .csv and .xlsx files are allowed.",
        );
        setSnackbarOpen(true);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSnackbarMsg("File size exceeds 5MB limit.");
        setSnackbarOpen(true);
        return;
      }
      processFile(file);
    });
  };

  const processFile = (file: File) => {
    const fileId = crypto.randomUUID?.() || `${Date.now()}-${file.name}`;
    const entry = {
      id: fileId,
      name: file.name,
      size: file.size,
      progress: 0,
      status: "Reading",
    };
    setUploadedFiles((prev) => [...prev, entry]);

    const markFileError = (errMsg: string) => {
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: "Error", progress: 0 } : f,
        ),
      );
      setSnackbarMsg(errMsg);
      setSnackbarOpen(true);
    };

    const markFileParsed = () =>
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, progress: 100, status: "Parsed" } : f,
        ),
      );

    const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
    if (isXlsx) {
      // XLSX path using read-excel-file
      parseXLSX(file)
        .then((rows) => {
          if (!rows.length) {
            markFileError("XLSX file is empty or not parseable.");
            return;
          }

          // Try transform all rows; if any fail -> mark error and do NOT add rows
          const transformedRows: any[] = [];
          for (const rawRow of rows) {
            const res = transformRow(rawRow, fileId);
            if (!res.success) {
              markFileError(`File "${file.name}": ${res.error}`);
              return;
            }
            transformedRows.push(res.row);
          }

          // success
          markFileParsed();
          setParsedRows((prev) => [...prev, ...transformedRows]);
        })
        .catch((err) => {
          console.error("XLSX parse error:", err);
          markFileError("Failed to parse XLSX file.");
        });
    } else {
      // CSV path
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = String(event.target?.result || "");
        const rawRows = parseCSV(text);
        if (!rawRows.length) {
          markFileError("CSV file is empty or not parseable.");
          return;
        }

        const transformedRows: any[] = [];
        for (const rawRow of rawRows) {
          const res = transformRow(rawRow, fileId);
          if (!res.success) {
            markFileError(`File "${file.name}": ${res.error}`);
            return;
          }
          transformedRows.push(res.row);
        }

        markFileParsed();
        setParsedRows((prev) => [...prev, ...transformedRows]);
      };
      reader.onerror = (err) => {
        console.error("CSV read error:", err);
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: "Error" } : f)),
        );
        setSnackbarMsg("Failed to read CSV file.");
        setSnackbarOpen(true);
      };
      reader.readAsText(file);
    }
  };

  // Remove file from uploadedFiles list
  const removeFile = (index: number) => {
    const fileToRemove = uploadedFiles[index];
    if (!fileToRemove) return;
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setParsedRows((prev) =>
      prev.filter((row) => row._fileId !== fileToRemove.id),
    );
  };

  return (
    <>
      <Grid container spacing={2} mt={2}>
        <Typography>Please upload your receipt file</Typography>
        <Grid size={12}>
          <Card
            sx={{
              marginTop: "16px",
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
            }}
          >
            <Paper
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: 2,
                padding: 4,
                backgroundColor: isDragging
                  ? "#f3e5f5"
                  : "rgba(156, 39, 176, 0.04)",
                border: isDragging
                  ? "1px dashed #9C27B0"
                  : "1px dashed #cacacaff",
                pointerEvents: "auto",
                cursor: "pointer",
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <UploadFileIcon color="secondary" fontSize="large" />
              <Typography
                component="label"
                sx={{ display: "flex", gap: 1, cursor: "pointer" }}
              >
                <Typography
                  color="secondary"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Click to upload
                </Typography>
                <input
                  type="file"
                  multiple
                  accept=".csv,.xlsx"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                  data-tracker="582"
                />
                or drag and drop
              </Typography>
              <Typography sx={{ color: "#9e9e9e" }}>
                CSV or XLSX (max. 5MB)
              </Typography>
            </Paper>
            {uploadedFiles?.length > 0 &&
              uploadedFiles?.map((file: any, index: any) => (
                <Card
                  key={index}
                  data-testid={`file-status-${index}`}
                  sx={{
                    padding: "8px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "16px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                  }}
                >
                  <div>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: "4px",
                      }}
                    >
                      <img
                        src={CsvIcon}
                        alt="csv icon"
                        width={20}
                        height={20}
                      />
                      <Typography variant="body2">{file.name}</Typography>
                    </Box>
                    <Typography data-testid={`file-status-${index}`}>
                      {(file.size / 1024).toFixed(2)} KB •{" "}
                      {file.status === "Parsed" ? "Complete" : file.status}
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      color="secondary"
                    />
                  </div>
                  <IconButton onClick={() => removeFile(index)}>
                    <CloseIcon sx={{ cursor: "pointer" }} />
                  </IconButton>
                </Card>
              ))}
          </Card>

          <Typography mt={2}>
            Want to see the correct format first?{" "}
            <Link
              color="secondary"
              href="/sample_file_finance_receipt.xlsx"
              download="sample_file_finance_receipt.xlsx"
            >
              Download our sample file.
            </Link>
          </Typography>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddReceiptsUploadFile;
